<template>
    
    <button
        class="inline-flex items-center px-4 py-2 border border-blue-500 text-blue-500 bg-white transition-colors duration-300 hover:bg-blue-500 hover:text-white rounded-md text-sm"
        @click="redirectToLastPage()"
    >
        <svg class="w-5 h-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        Retour
    </button>
    
    <div class="card">
        <DataTable
            lazy
            :value="tableData"
            :paginator="true"
            :rows="rows"
            :rowHover="true"
            :loading="isLoading"
            :totalRecords="totalRecords"
            :currentPage="currentPage"
            :rowsPerPageOptions="rowsPerPageOptions"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} produits"
            @page="onPaginate($event)">
            <template #header>
                <div class="flex flex-row flex-nowrap card-header border-0">
                    <div class="flex card-toolbar w-full">
                        <div class="w-full">
                            <span class="p-input-icon-left w-10/12">
                                <i class="pi pi-search" />
                                <InputText type="text" class="w-9/12" v-model="criteria" placeholder="Rechercher un produit" @keyup.enter="onSearchInputDt()" />
                            </span>
                        </div>
                    </div>

                    <div class="card-toolbar w-full">
                        <div class="flex flex-col justify-center">
                            <p>Nombre total de produits : {{ totalRecords }}</p>
                            <p v-if="isInFilteredView">Vous êtes en vue filtrée</p>
                        </div>
                    </div>

                    <div class="card-toolbar w-full">
                        <FormKit
                            type="taglist"
                            name="selected-category-filter"
                            label="Rechercher une catégorie"
                            placeholder="Example: E-billet"
                            v-model="selectedOptionsCategory"
                            :options="searchCategories"
                            selection-appearance="option"
                            multiple
                            empty-message="Aucune catégorie trouvée"
                            load-on-created="false"
                            loadOnScroll="false"
                            debounce="1000"
                        >
                        </FormKit>
                    </div>
                </div>
            </template>
            <div class="card-body pt-0">
                <Column field="name" header="Produit">
                    <template #body="slotProps">
                        <span>{{ slotProps.data.name }}</span>
                    </template>
                </Column>
                <Column field="brand" header="Marque">
                    <template #body="slotProps">
                        <span>{{ slotProps.data.brand }}</span>
                    </template>
                </Column>
                <Column field="category" header="Catégorie">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.categories?.length > 0" :title="formatCategoriesProduct(slotProps.data.categories)">
                            {{ slotProps.data.categories[0].value }}
                        </span>
                    </template>
                </Column>
            </div>
            <template #empty>Aucun produit trouvé.</template>
        </DataTable>
    </div>
</template>

<script setup lang="ts">
import { fetchVariantProductsByPropertyValue, formatCategoriesProduct } from "@/_services/product/variant/productVariant";
import { onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { IProductVariantForProperty } from "@/models/product/variant/IProductVariantForProperty";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import { fetchCategoriesForDropDown } from "@/_services/category/category";
import router from "@/router";

const isInFilteredView = ref<boolean>(false);
const productVariants = Array<IProductVariantForProperty>();
const rows = ref<number>(10);
const page = ref < number > (1);
const criteria = ref<string>("");
const currentPage = ref<number>(0);
const defaultRowsPerPage = 10;
const rowsPerPageOptions = [defaultRowsPerPage, 25, 100];
const totalRecords = ref<number>(0);
const isLoading = ref<boolean>(true);
const tableData = ref<Array<IProductVariantForProperty>>(productVariants);
const route = useRoute();
const propertyValueUuidRouteParam = ref<string | null>(null);
const selectedOptionsCategory = ref<string[]>([]);

onMounted(async () => {
    propertyValueUuidRouteParam.value = route.params.uuid as string;
    if (propertyValueUuidRouteParam.value) {
        await populateDatatable(rows.value, page.value, criteria.value);
    }
});

watch(selectedOptionsCategory, () => {
    isInFilteredView.value = selectedOptionsCategory.value.length > 0;
    handleSelectChange();
});

async function searchCategories({ search, page, hasNextPage }) {
    const [totalCategoriesRecordsForPickListApi, categoriesForPickListApi] = await fetchCategoriesForDropDown(5, page, search);
    const hasItems = categoriesForPickListApi.length > 0;
    if (hasItems && page !== totalCategoriesRecordsForPickListApi) {
        hasNextPage();
    }
    
    return categoriesForPickListApi;
}

const populateDatatable = async (
    itemsPerPage: number,
    page: number,
    criteria: string,
    categoriesFilter?: string[]
) => {
    const [loadingDt, totalRecordsDt, tableDataDt] = await fetchVariantProductsByPropertyValue(
        itemsPerPage,
        page,
        criteria,
        true,
        propertyValueUuidRouteParam.value,
        categoriesFilter
    );
    totalRecords.value = totalRecordsDt;
    isLoading.value = loadingDt;
    tableData.value = tableDataDt;
};

const handleSelectChange = async () => {
    showSpinner();
    await populateDatatable(
        rows.value,
        page.value,
        "" != criteria.value ? criteria.value : "",
        selectedOptionsCategory.value
    );
};

const onPaginate = async (e) => {
    showSpinner();
    rows.value = e.rows;
    currentPage.value = e.page;
    page.value = e.page + 1;
    await populateDatatable(e.rows, e.page + 1, criteria.value, selectedOptionsCategory.value);
};

const onSearchInputDt = () => {
    showSpinner();
    let search = criteria.value.trim();
    populateDatatable(
        rows.value,
        page.value,
        "" != search ? search : "",
        selectedOptionsCategory.value
    );
}

const redirectToLastPage = () => {
    router.back();
};

const showSpinner = () => {
    isLoading.value = true;
}
const hideSpinner = () => {
    isLoading.value = false;
}
</script>

<style scoped lang="scss">


.returnLink {
    @apply hover:bg-[#0275ff] hover:text-white bg-white text-[#0275ff]
    transition-all duration-300 p-5 border-[1px] border-[#0275ff] rounded-md;
}
select {
  @apply font-normal;
}
.card-body {
    @apply bg-white;
}
::v-deep(.p-datatable .p-datatable-tbody > tr.p-datatable-emptymessage > td ) {
    text-align: center;
}
::v-deep(.p-datatable .p-datatable-tbody > tr.p-datatable-emptymessage > td ) {
    text-align: center;
}
::v-deep(.hover\:bg-gray-300:hover) {
    cursor: pointer;
}
</style>